import { queryOptions, useQuery } from '@tanstack/react-query';

import { DateRange } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';

const NAMESPACE_REPORT_QUERY_KEY = 'namespace-report';

const getNamespaceQueryKey = ({
  clusterId,
  namespace,
  startTime,
  endTime,
  useListingPrices,
  includeIdleCosts,
}: {
  clusterId?: string;
  namespace?: string;
  startTime?: string;
  endTime?: string;
  useListingPrices?: boolean;
  includeIdleCosts?: boolean;
} = {}) =>
  [
    NAMESPACE_REPORT_QUERY_KEY,
    ...(clusterId !== undefined
      ? [
          namespace,
          clusterId,
          startTime,
          endTime,
          useListingPrices,
          includeIdleCosts,
        ]
      : []),
  ] as const;

type NamespaceQuery = {
  clusterId: string;
  namespace: string;
  range: DateRange;
  useListingPrices: boolean;
  includeIdleCosts?: boolean;
  enabled?: boolean;
};

const getNamespaceQueryOptions = ({
  clusterId,
  namespace,
  range: [from, to],
  useListingPrices,
  includeIdleCosts = false,
  enabled = true,
}: NamespaceQuery) =>
  queryOptions({
    queryKey: getNamespaceQueryKey({
      clusterId,
      namespace,
      startTime: from.toISOString(),
      endTime: to.toISOString(),
      useListingPrices,
      includeIdleCosts,
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getNamespace(
        {
          clusterId,
          namespace,
          startTime: from.toISOString(),
          endTime: to.toISOString(),
          useListingPrices,
          includeIdleResourceCosts: includeIdleCosts,
        },
        { timeout: 30_000 }
      );

      return data;
    },
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

export const useNamespaceQuery = (props: NamespaceQuery) => {
  const { data, isLoading, error, refetch } = useQuery(
    getNamespaceQueryOptions(props)
  );

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
